import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { FollowUpRequestBoardDetailModal } from '@/follow-up/FollowUpRequestBoardDetailModal';
import FollowUpRequestBoardTable from '@/tables/FollowUpRequestBoardTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';

export const Route = createFileRoute('/_app/follow-up-request-board/')({
  component: FollowUpRequestBoardScreen,
});

/**
 * The network-wide Follow-up Request Board —
 * `themes/wbs/templates/page-follow-up-request-board.php`.
 *
 * A follow-up coordinator pulls work from here: every approved follow-up
 * request that no coordinator has claimed and that has not become a follow-up,
 * defaulted to their own approved countries and student languages. Taking one
 * writes the coordinator claim onto the request, which is the grant of an
 * authorization relation — `patterns/permissions/students.py` gates fifteen
 * call sites on exactly the column Take writes.
 *
 * Legacy's own page has NO gate: `page-follow-up-request-board.php:16`
 * constructs the renderer without calling `check_requirements()`, and the
 * datatable ajax it feeds is registered `wp_ajax_nopriv` — an anonymous POST
 * with a bogus nonce reaches the query and gets a normal datatable envelope,
 * though DIV-4's empty-axis default leaves it row-less. The table class
 * DECLARES the coordinator-or-admin gate (`FollowUpRequestsBoard.php:67-74`)
 * and only the fullapp controller ever ran it, so porting it is a tightening
 * (DIV-1).
 */
function FollowUpRequestBoardScreen() {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();
  const [detailRequestId, setDetailRequestId] = useState<number | null>(null);

  // THE GATE IS `profile_type`, AND THE TWO HOOKS THAT LOOK RIGHT ARE BOTH
  // WRONG. `useCurrentUser().isStudyHelper` folds FOLLOWUP_COORDINATOR,
  // FOLLOWUP_PARTNER and ADMIN together (`hooks/useCurrentUser.ts:14-19`), so
  // it admits 846 study helpers and 89 partners the endpoint refuses; and
  // `useIsCoordinator` reads `params.groupName` and disables its query without
  // one (`hooks/useIsCoordinator.ts:33-47`), so on a top-level route its
  // `isResolved` never flips and `isCoordinator` is false forever.
  //
  // `isResolved` is not optional either: `/users/me` is pending on first paint
  // and a bare `!canView` bounces every caller, the coordinator included.
  //
  // This is defence in depth over the same set the endpoints enforce, never the
  // only gate — the list, the detail and Take each refuse on their own.
  const canView =
    me?.profile_type === 'FOLLOWUP_COORDINATOR' || me?.profile_type === 'ADMIN';

  if (isPending) return null;
  if (!canView) return <NotFoundScreen />;

  return (
    <PageContainer>
      <div style={{ margin: '9px 0 29px 0' }}>
        <TitleWithQuestion title={t('wbs.follow-up-request-board')} />
      </div>
      <FollowUpRequestBoardTable onViewDetail={setDetailRequestId} />
      <FollowUpRequestBoardDetailModal
        requestId={detailRequestId}
        onClose={() => setDetailRequestId(null)}
      />
    </PageContainer>
  );
}
